import { todayKey } from './attendance-local';

/**
 * ⑥ 상태바 근무 상태 — 2026-08-12 사용자 요구.
 *
 *   "상태바에서 상태를 클릭하면 출근, 퇴근, 회의 등의 상태가 표시되도록 수정"
 *
 * 종전 상태바 맨 왼쪽의 "상태" 는 점 하나 + 글자뿐인 **장식**이었다(`.status-bar__item--status`
 * 가 `cursor:default`). 그걸 누를 수 있게 바꾸고, 고른 상태를 그 자리에 그대로 표시한다.
 * 와이어프레임(`docs/all-frame.html` #2a 상태바)도 첫 칸을 강조색 칩 + "회의중" 으로 그린다.
 *
 * 상태 구성 근거:
 *   - 사용자가 명시한 **출근 · 퇴근 · 회의**
 *   - 와이어프레임 프로필 편집(#1d) "근무 상태" 의 자리비움
 *   - 위 넷만으로는 외부 일정이 표현 안 돼 **외근** 추가(HR 의 business_trip/outside_work 와 같은 은유)
 *
 * 저장 위치가 둘로 나뉜다 — 의도된 분리다:
 *   - 출근/퇴근 = **서버 근태 기록**(`/hr/attendance/check-in|check-out`). 사실 기록이라
 *     기기를 옮겨도 따라가야 하고, 이미 서버 라우트가 있다.
 *   - 회의중/자리비움/외근 = **브라우저 로컬**. 서버에 "현재 근무 상태" 컬럼이 없어서다
 *     (users 스키마 무변경 = 마이그레이션 없이 배포 가능). 표시용 상태라 하루 단위로 리셋한다.
 *
 * 하루 경계: 저장 값에 날짜를 함께 남겨 **날짜가 바뀌면 미설정**으로 돌아간다. 어제 걸어둔
 * "회의중" 이 오늘 아침까지 붙어 있으면 상태바가 거짓말을 한다.
 */

export type WorkStatusKey = 'work' | 'meeting' | 'away' | 'out' | 'trip' | 'off';

/**
 * 드롭다운 표시 순서 — 하루의 흐름(출근 → 업무 중 변형 → 퇴근).
 *
 * 2026-08-12 추가 — **출장**(`trip`). 프로필 팝오버의 "근태 설정" 요구("출근, 퇴근, 출장 등")에서
 * 왔다. 외근(`out`)이 "오늘 잠깐 밖" 이라면 출장은 "며칠 자리 비움" 이라 사용자 인식이 다르다
 * (HR 모듈도 `business_trip` / `outside_work` 를 따로 둔다 — `hr-helpers.TRIP_TYPES`).
 * 외근과 같은 로컬 표시 상태라 서버 스키마·API 변경은 없다.
 */
export const WORK_STATUS_KEYS: readonly WorkStatusKey[] = ['work', 'meeting', 'away', 'out', 'trip', 'off'];

/** 서버 근태 기록으로 결정되는 상태(로컬 저장이 아니라 서버가 진실). */
export const SERVER_BACKED_STATUSES: readonly WorkStatusKey[] = ['work', 'off'];

/**
 * ⑥ 상태바 드롭다운이 **고르게 하는** 항목 — 2026-08-20 사용자 요구
 * ("근무상태도 출근, 퇴근 이렇게 보이면 되는데").
 *
 * 회의중·자리비움·외근·출장을 **없앤 것이 아니라 이 목록에서만 뺐다.** 프로필 팝오버는
 * `WORK_STATUS_KEYS` 를 그대로 쓰고, 거기서 고른 값은 상태바에도 그대로 표시된다
 * (표시는 `resolveDisplayStatus` 가 담당하고, 이 상수는 선택 목록에만 쓰인다).
 */
export const STATUS_BAR_KEYS: readonly WorkStatusKey[] = ['work', 'off'];

export function isWorkStatusKey(v: unknown): v is WorkStatusKey {
  return typeof v === 'string' && (WORK_STATUS_KEYS as readonly string[]).includes(v);
}

export function isServerBacked(key: WorkStatusKey): boolean {
  return (SERVER_BACKED_STATUSES as readonly string[]).includes(key);
}

/**
 * 퇴근을 잠글 것인가 — **"퇴근은 출근한 사람만"**(근태 2차 설계 요구 3 · 화면 E, 2026-08-23).
 *
 * 서버는 이미 그렇게 동작한다 — 오늘 출근 기록이 없으면 `NOT_CHECKED_IN` 으로 400 을 준다
 * (`apps/chat-server/src/modules/hr/attendance.routes.ts:158`). 규칙은 이미 맞고, 없던 것은
 * **화면이 미리 말해 주는 것**이었다. 지금까지는 눌러 봐야 실패를 알았다.
 *
 * ★ 오늘 기록을 아직 못 읽었으면(`hydrated=false`) **잠그지 않는다.** 조회가 실패했을 뿐인데
 *   실제로 출근한 사람의 퇴근까지 막으면 사실 기록이 늦어진다 — 모를 때는 종전대로 서버가
 *   판단하게 두고(400 이 오면 사유를 보여 준다), 아는 때에만 앞질러 막는다.
 */
export function isCheckOutLocked(
  input: { hydrated: boolean; checkIn: string | null; deemed?: boolean },
): boolean {
  // 간주 출근일(승인된 출장·외근)은 **출근한 것으로 본다** — 그날은 잠그지 않는다 (근태 D2,
  // 2026-08-24). 서버도 같은 판정이라 여기서만 열어 두면 눌러도 400 이 나는 일이 없다.
  if (input.deemed) return false;
  return input.hydrated && !input.checkIn;
}

/**
 * 상태 → 색 토큰 계열. CSS 는 `is-<tone>` 클래스로 받는다.
 * (이모지 금지 정책 — 표시는 인라인 SVG 아이콘 + 점 색으로만 구분한다.)
 */
export type WorkStatusTone = 'active' | 'busy' | 'idle' | 'away' | 'off';

export const WORK_STATUS_TONE: Record<WorkStatusKey, WorkStatusTone> = {
  work: 'active',
  meeting: 'busy',
  away: 'idle',
  out: 'away',
  // 출장도 '자리에 없음' 계열 — 외근과 같은 색을 쓴다(둘을 다른 색으로 나누면 점만 보고는
  // 구분이 안 되면서 색만 늘어난다). 구분은 라벨이 한다.
  trip: 'away',
  off: 'off',
};

// ─── 로컬 저장 ─────────────────────────────────────────────

const KEY = 'axi.workstatus.v1';

interface StoredWorkStatus {
  /** YYYY-MM-DD — 저장 시점의 로컬 날짜. 다르면 만료로 본다. */
  date: string;
  status: WorkStatusKey;
}

/** 오늘 저장된 상태. 미설정·날짜 경과·파싱 실패는 전부 null(미설정)로 수렴. */
export function readStoredStatus(now: Date = new Date()): WorkStatusKey | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredWorkStatus>;
    if (parsed?.date !== todayKey(now)) return null;
    return isWorkStatusKey(parsed.status) ? parsed.status : null;
  } catch {
    return null;
  }
}

/** 상태 저장. null 이면 기록을 지운다(미설정으로 되돌리기). */
export function writeStoredStatus(status: WorkStatusKey | null, now: Date = new Date()): void {
  try {
    if (status === null) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify({ date: todayKey(now), status } satisfies StoredWorkStatus));
  } catch {
    /* 저장 실패는 무시 — 화면 상태(스토어)는 그대로 반영된다. */
  }
}

// ─── 서버 근태 기록 → 상태 ─────────────────────────────────

/**
 * 오늘 근태 기록에서 출근/퇴근 상태를 유도한다.
 *   퇴근 시각 있음 → 'off' · 출근 시각만 있음 → 'work' · 둘 다 없음 → null(미설정)
 */
export function deriveStatusFromAttendance(
  rec: { checkInAt: string | null; checkOutAt: string | null } | null | undefined,
): WorkStatusKey | null {
  if (!rec) return null;
  if (rec.checkOutAt) return 'off';
  if (rec.checkInAt) return 'work';
  return null;
}

/**
 * 로컬 표시 상태와 서버 근태 기록을 합친 최종 표시 상태.
 *
 * 규칙: **로컬이 우선**이되, 로컬이 서버가 진실인 상태(출근/퇴근)를 들고 있으면 서버 값으로
 * 교정한다. 예) 다른 기기에서 퇴근을 찍었으면 이 브라우저의 "출근" 은 "퇴근" 으로 정정된다.
 * 반대로 로컬이 "회의중" 이면 서버 기록(출근함)보다 구체적인 정보라 그대로 둔다.
 */
export function resolveDisplayStatus(
  local: WorkStatusKey | null,
  fromServer: WorkStatusKey | null,
): WorkStatusKey | null {
  if (local === null) return fromServer;
  if (isServerBacked(local)) return fromServer ?? local;
  return local;
}

/**
 * 셋을 합친 최종 표시 상태 — **서버 이력이 생긴 뒤의 규칙** (2026-09-06 · 판 C).
 *
 * 종전에는 회의중·자리비움이 **브라우저에만** 있어 로컬이 우선이었다. 이제 그 상태도
 * 서버 이력(`hr_work_status_logs`)에 남으므로 **서버가 말하는 것이 진실**이다 —
 * 다른 기기에서 「자리비움」으로 바꿨으면 이 브라우저도 그렇게 보여야 한다.
 *
 * 규칙(위에서부터):
 *   ① **퇴근했으면 그것이 오늘의 끝**이다 — 「회의중인 채로 퇴근」은 상태바가 거짓말을 하는 것이다.
 *   ② 서버 이력이 말하는 것이 있으면 그것.
 *   ③ 없으면 종전 규칙(`resolveDisplayStatus`) — **옛 서버는 이력을 안 준다**(그때 화면이
 *      종전과 똑같이 돌아야 한다).
 *
 * @param fromAttendance 오늘 근태 기록에서 유도한 값(`work` · `off` · null).
 * @param fromLog        서버 이력의 **마지막 말**. 옛 서버면 null.
 */
export function mergeWorkStatus(
  local: WorkStatusKey | null,
  fromAttendance: WorkStatusKey | null,
  fromLog: WorkStatusKey | null,
): WorkStatusKey | null {
  if (fromAttendance === 'off') return 'off';
  if (fromLog) return fromLog;
  return resolveDisplayStatus(local, fromAttendance);
}
